import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useAmplitudeTracker } from "../../../services/hooks/useAmplitudeTracker";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { PagesEnum } from "../../../utils/enums";
import { isVisitor } from "../../../utils/help/user/user";
import { Restaurant } from "../../../utils/types";
import FacebookPixel from "../../FacebookPixel/FacebookPixel";
import { AnimatedGlobalBanner } from "../../global/Banner/AnimatedGlobalBanner";
import SessionTimeoutPopup from "../../global/Modal/Modals/SessionTimeoutPopup/SessionTimeoutPopup";
import { useAuth } from "../auth/useAuth";

const Footer = dynamic(
  async () => {
    try {
      const module = await import(
        `../../global/NewFooter/${"FooterComponent"}`
      );

      return module.default;
    } catch (e) {
      console.error(e);
    }
  },
  { ssr: false }
);
const Header = dynamic<{ restaurantInfo?: Restaurant }>(
  async () => {
    try {
      const module = await import(
        `../../global/NewHeader/${"HeaderComponent"}`
      );

      return module;
    } catch (e) {
      console.error(e);
    }
  },
  { ssr: false }
);

export const MainLayout = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useRouter();
  const [isIdle, setIsIdle] = useState<boolean>(false);

  useAmplitudeTracker();

  const selfService = useAppSelector(
    (state) => state.franchiseStatus.selfService
  );
  const layout = useAppSelector((state) => state.layout.layoutProps);
  const globalBanner = useAppSelector(
    (state: any) => state.globalComponentsToggleSlice.animatedGlobalBanner
  );
  const isLoading = useAppSelector(
    (state: any) => state.globalComponentsToggleSlice.loading
  );
  const pageContentMargin = useAppSelector(
    (store) => store.layout.pageContentMargin
  );
  const selfServiceConfigs = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig?.selfService
  );

  const idleTimeoutSeconds =
    (isVisitor()
      ? selfServiceConfigs?.sessionPopupTimeOutDisplay || 30
      : selfServiceConfigs?.loggedInSessionPopupTimeOutDisplay || 5) * 1000;

  const authApp = useAuth();
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timeToLogout = selfServiceConfigs?.sessionPopupTimeOutLogout || 10;

  useEffect(() => {
    const events = ["mousemove", "keydown", "scroll", "click"];
    events.forEach((event) =>
      window.addEventListener(event, () => {
        resetIdleTimer();
      })
    );

    resetIdleTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetIdleTimer)
      );
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [pathname]);

  useEffect(() => {
    resetIdleTimer();
  }, [idleTimeoutSeconds, pathname]);

  const resetIdleTimer = () => {
    if (
      isIdle ||
      pathname.includes(PagesEnum.PaymentStatus) ||
      pathname.includes(PagesEnum.SelfService) ||
      !selfService ||
      isLoading
    ) {
      return;
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    if (
      idleTimeoutSeconds &&
      !pathname.includes(PagesEnum.PaymentStatus) &&
      !pathname.includes(PagesEnum.SelfService) &&
      selfService &&
      !isLoading
    ) {
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
      }, idleTimeoutSeconds);
    }
  };

  const handleContinueSession = () => {
    setIsIdle(false);
    resetIdleTimer();
  };

  const handleEndSession = async () => {
    setIsIdle(false);
    await authApp.signOut();
  };

  return (
    <>
      <FacebookPixel />
      {globalBanner.isOpen && <AnimatedGlobalBanner />}
      {layout.headerProps.display && <Header />}
      <div
        className="relative"
        style={{
          marginTop:
            layout.headerProps.display &&
            !pathname.includes(PagesEnum.OfflineMode)
              ? pageContentMargin
              : 0,
        }}
      >
        {isIdle && (
          <SessionTimeoutPopup
            onContinue={handleContinueSession}
            onEndSession={handleEndSession}
            countdownStart={timeToLogout}
          />
        )}
        {children}
      </div>
      {layout.footerProps.display ? <Footer /> : null}
    </>
  );
};
